<template>
  <div class="language-switcher" 
       @mouseenter="handleMouseEnter" 
       @mouseleave="handleMouseLeave"
       @click="handleClick">
    <button 
      @click="toggleDropdown" 
      class="language-btn"
      :title="currentLanguageInfo.name"
    >
      <span class="flag">{{ currentLanguageInfo.flag }}</span>
      <span class="text">{{ currentLanguageInfo.code === 'zh-TW' ? '中文' : 'EN' }}</span>
      <span class="arrow" :class="{ 'arrow-up': showDropdown }">▼</span>
    </button>
    
    <!-- 语言选择列表 -->
    <div class="language-dropdown" 
         v-show="showDropdown"
         @mouseenter="handleDropdownMouseEnter"
         @mouseleave="handleDropdownMouseLeave"
         @click.stop>
      <div 
        v-for="language in availableLanguages" 
        :key="language.code"
        class="language-option"
        :class="{ 'active': language.code === currentLanguage }"
        @click="selectLanguage(language.code)"
      >
        <span class="flag">{{ language.flag }}</span>
        <span class="name">{{ language.name }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref, onUnmounted } from 'vue'
import { useStore } from 'vuex'

export default {
  name: 'LanguageSwitcher',
  setup() {
    const store = useStore()
    const showDropdown = ref(false)
    const isMobile = ref(false)
    const isHovering = ref(false)

    const currentLanguageInfo = computed(() => store.getters['language/getCurrentLanguageInfo'])
    const currentLanguage = computed(() => store.getters['language/getCurrentLanguage'])
    const availableLanguages = computed(() => store.getters['language/getAvailableLanguages'])

    // 检测是否为移动设备
    const checkMobile = () => {
      isMobile.value = window.innerWidth <= 768
    }

    const toggleDropdown = () => {
      // 移动端点击切换，桌面端也允许点击切换
      showDropdown.value = !showDropdown.value
    }

    const handleMouseEnter = () => {
      if (!isMobile.value) {
        isHovering.value = true
        showDropdown.value = true
      }
    }

    const handleMouseLeave = () => {
      if (!isMobile.value) {
        isHovering.value = false
        // 延迟关闭，给用户时间移动到下拉框
        setTimeout(() => {
          if (!isHovering.value) {
            showDropdown.value = false
          }
        }, 100)
      }
    }

    const handleDropdownMouseEnter = () => {
      if (!isMobile.value) {
        isHovering.value = true
      }
    }

    const handleDropdownMouseLeave = () => {
      if (!isMobile.value) {
        isHovering.value = false
        showDropdown.value = false
      }
    }

    const handleClick = (e) => {
      // 阻止事件冒泡
      e.stopPropagation()
    }

    const selectLanguage = (languageCode) => {
      store.dispatch('language/switchLanguage', languageCode)
      showDropdown.value = false
    }

    // 处理滚动事件
    const handleScroll = () => {
      if (isMobile.value && showDropdown.value) {
        showDropdown.value = false
      }
    }

    onMounted(() => {
      // 初始化语言设置
      store.dispatch('language/initLanguage')
      
      // 检测设备类型
      checkMobile()
      window.addEventListener('resize', checkMobile)
      
      // 添加滚动事件监听
      window.addEventListener('scroll', handleScroll, true)
    })

    onUnmounted(() => {
      // 清理事件监听
      window.removeEventListener('resize', checkMobile)
      window.removeEventListener('scroll', handleScroll, true)
    })

    return {
      currentLanguageInfo,
      currentLanguage,
      availableLanguages,
      showDropdown,
      isMobile,
      toggleDropdown,
      handleMouseEnter,
      handleMouseLeave,
      handleDropdownMouseEnter,
      handleDropdownMouseLeave,
      handleClick,
      selectLanguage
    }
  }
}
</script>

<style scoped>
.language-switcher {
  position: fixed;
  bottom: 16px;
  left: 16px;
  z-index: 1002;
}

.language-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 7px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 13px;
  font-weight: 500;
  color: #2c3e50;
  position: relative;
  min-height: 28px;
  min-width: 56px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.language-btn:hover,
.language-btn:focus {
  background: #f8f9fa;
  border-color: #2c3e50;
  box-shadow: 0 4px 12px rgba(44, 62, 80, 0.15);
}

.flag {
  font-size: 15px;
  margin-right: 3px;
}

.text {
  font-weight: 600;
  color: #2c3e50;
}

.arrow {
  font-size: 10px;
  margin-left: 2px;
  color: #666666;
  transition: transform 0.2s;
}

.arrow-up {
  transform: rotate(180deg);
}

.language-dropdown {
  position: absolute;
  bottom: calc(100% + 6px);
  left: 0;
  background: #ffffff;
  border-radius: 6px;
  min-width: 85px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  border: 1px solid #e0e0e0;
  animation: dropdownFadeIn 0.18s;
}

.language-option {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  cursor: pointer;
  color: #424242;
  font-size: 13px;
  font-weight: 400;
  background: none;
  border: none;
  transition: background 0.15s, color 0.15s;
}

.language-option:hover {
  background: #f8f9fa;
  color: #2c3e50;
}

.language-option.active {
  background: #ecf0f1;
  color: #2c3e50;
  font-weight: 900;
}

.language-option .flag {
  font-size: 15px;
  margin-right: 3px;
}

.language-option .name {
  font-size: 13px;
  font-weight: inherit;
}

@keyframes dropdownFadeIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 768px) {
  .language-switcher {
    bottom: 10px;
    left: 10px;
  }
  .language-btn {
    padding: 3px 8px;
    font-size: 12px;
    border-radius: 6px;
    min-width: 44px;
    min-height: 22px;
  }
  .flag {
    font-size: 13px;
  }
  .language-dropdown {
    min-width: 60px;
    border-radius: 5px;
  }
  .language-option {
    padding: 4px 8px;
    font-size: 12px;
    min-width: 60px;
  }
  .language-option .name {
    font-size: 11px;
  }
}
</style> 